import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import * as FileSaver from 'file-saver'
import * as XLS from 'xlsx'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../component/Axios'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import Pagination from '../../component/Pagination'
import ResetFilterButton from '../../component/ResetFilterButton'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import Title from '../../component/Title'
import { LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_SEARCH_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { formatDate, formatDateTimeText } from '../../utils/dateDisplay'
import { createInitialListState, buildListSearch as buildListSearchQuery } from '../../utils/listPageState'

const SEOUL25_REGIONS = [
    { regionCode: 'SEOUL25_GANGNAM', regionName: '강남구', codePrefix: 'GN' },
    { regionCode: 'SEOUL25_GANGDONG', regionName: '강동구', codePrefix: 'GD' },
    { regionCode: 'SEOUL25_GANGBUK', regionName: '강북구', codePrefix: 'GB' },
    { regionCode: 'SEOUL25_GANGSEO', regionName: '강서구', codePrefix: 'GS' },
    { regionCode: 'SEOUL25_GWANAK', regionName: '관악구', codePrefix: 'GA' },
    { regionCode: 'SEOUL25_GWANGJIN', regionName: '광진구', codePrefix: 'GJ' },
    { regionCode: 'SEOUL25_GURO', regionName: '구로구', codePrefix: 'GR' },
    { regionCode: 'SEOUL25_GEUMCHEON', regionName: '금천구', codePrefix: 'GC' },
    { regionCode: 'SEOUL25_NOWON', regionName: '노원구', codePrefix: 'NW' },
    { regionCode: 'SEOUL25_DOBONG', regionName: '도봉구', codePrefix: 'DB' },
    { regionCode: 'SEOUL25_DONGDAEMUN', regionName: '동대문구', codePrefix: 'DD' },
    { regionCode: 'SEOUL25_DONGJAK', regionName: '동작구', codePrefix: 'DJ' },
    { regionCode: 'SEOUL25_MAPO', regionName: '마포구', codePrefix: 'MP' },
    { regionCode: 'SEOUL25_SEODAEMUN', regionName: '서대문구', codePrefix: 'SM' },
    { regionCode: 'SEOUL25_SEOCHO', regionName: '서초구', codePrefix: 'SC' },
    { regionCode: 'SEOUL25_SEONGDONG', regionName: '성동구', codePrefix: 'SD' },
    { regionCode: 'SEOUL25_SEONGBUK', regionName: '성북구', codePrefix: 'SB' },
    { regionCode: 'SEOUL25_SONGPA', regionName: '송파구', codePrefix: 'SP' },
    { regionCode: 'SEOUL25_YANGCHEON', regionName: '양천구', codePrefix: 'YC' },
    { regionCode: 'SEOUL25_YEONGDEUNGPO', regionName: '영등포구', codePrefix: 'YP' },
    { regionCode: 'SEOUL25_YONGSAN', regionName: '용산구', codePrefix: 'YS' },
    { regionCode: 'SEOUL25_EUNPYEONG', regionName: '은평구', codePrefix: 'EP' },
    { regionCode: 'SEOUL25_JONGNO', regionName: '종로구', codePrefix: 'JR' },
    { regionCode: 'SEOUL25_JUNG', regionName: '중구', codePrefix: 'JG' },
    { regionCode: 'SEOUL25_JUNGNANG', regionName: '중랑구', codePrefix: 'JN' },
]

const DEFAULT_SEARCH_SELECT = {
    packageCode: 'SEOUL25',
    regionCode: '',
    channel: '',
    status: '',
    batchNo: '',
}

const SEARCH_SELECT_PARAMS = {
    packageCode: { param: 'packageCode', defaultValue: DEFAULT_SEARCH_SELECT.packageCode },
    regionCode: { param: 'regionCode', defaultValue: DEFAULT_SEARCH_SELECT.regionCode },
    channel: { param: 'channel', defaultValue: DEFAULT_SEARCH_SELECT.channel },
    status: { param: 'status', defaultValue: DEFAULT_SEARCH_SELECT.status },
    batchNo: { param: 'batchNo', defaultValue: DEFAULT_SEARCH_SELECT.batchNo },
}

const DEFAULT_GENERATION_FORM = {
    packageCode: 'SEOUL25',
    regionCode: '',
    channel: 'RM',
    batchNo: '',
    quantity: '',
}

const CODE_TABLE_COLUMNS = [
    { width: 72 },
    { width: 240 },
    { width: 170 },
    { width: 90 },
    { width: 220 },
    { width: 130 },
    { width: 110 },
    { width: 120 },
    { width: 150 },
    { width: 150 },
]

const CODE_TABLE_SX = {
    minWidth: CODE_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0),
    tableLayout: 'fixed',
    '& th': {
        whiteSpace: 'nowrap',
    },
    '& td': {
        verticalAlign: 'middle',
    },
}

const TITLE_TEXT_SX = {
    display: '-webkit-box',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    lineHeight: 1.45,
    wordBreak: 'break-word',
}

const NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const BATCH_NO_CELL_SX = {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-all',
    lineHeight: 1.35,
}

const CODE_TEXT_SX = {
    fontFamily: 'monospace',
    fontWeight: 700,
    whiteSpace: 'nowrap',
}

const generationPanelSx = {
    mb: 2,
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    backgroundColor: 'background.default',
}

const generationFormSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 1,
}

const resultCodeListSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mt: 1,
}

const getInitialListState = (location) => createInitialListState({
    location,
    defaultSearchSelect: DEFAULT_SEARCH_SELECT,
    selectParams: SEARCH_SELECT_PARAMS,
})

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => buildListSearchQuery({
    curPage,
    searchQuery,
    searchSelect,
    selectParams: SEARCH_SELECT_PARAMS,
})

const getTotalCount = (count) => Number(count || 0)

const getRegionLabel = (regionCode, fallbackName) => {
    const region = SEOUL25_REGIONS.find((item) => item.regionCode === regionCode)

    if (region) {
        return `${region.regionName} (${region.codePrefix})`
    }

    return fallbackName || regionCode || '-'
}

const getStatusChip = (status) => {
    if (status === 'generated') {
        return <StatusChip label="생성" color="info" />
    }

    if (status === 'activated') {
        return <StatusChip label="활성화" color="success" />
    }

    if (status === 'void') {
        return <StatusChip label="폐기" color="default" variant="outlined" />
    }

    return <StatusChip label={status || '-'} variant="outlined" />
}

const getChannelChip = (channel) => (
    <StatusChip label={channel || '-'} color={channel === 'KAKAO' ? 'warning' : 'primary'} variant="outlined" />
)

const renderTitle = (title) => (
    <Box component="span" title={title || '-'} sx={TITLE_TEXT_SX}>
        {title || '-'}
    </Box>
)

export default function AnimalPackageCodes() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [generationLoading, setGenerationLoading] = useState(false)
    const [excelLoading, setExcelLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [generationForm, setGenerationForm] = useState({ ...DEFAULT_GENERATION_FORM })
    const [generationResult, setGenerationResult] = useState(null)
    const offset = 10
    const isBusy = actionLoading || generationLoading || excelLoading

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setActionLoading(true)
        setListErrorMessage('')

        try {
            const { data: response } = await API.post('/api/v1/admin/animalPackage/getCodeList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect,
            })

            if ([1, 200].includes(Number(response.code))) {
                setTotalCount(getTotalCount(response.data?.count))
                setData(response.data?.rows || [])
                setListErrorMessage('')
            } else if (response.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = response.message || '패키지 코드 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '패키지 코드 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
            setActionLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const showSnackbar = (severity, message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar(severity)
        setMessageSnackbar(message)
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect((prevSearchSelect) => ({
            ...prevSearchSelect,
            [name]: value,
        }))
    }

    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
        }

        setSearchQuery(trimmedSearchText)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
    }

    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    const updateGenerationForm = (e) => {
        const { name, value } = e.target

        if (generationLoading) {
            return
        }

        setGenerationForm((prevGenerationForm) => ({
            ...prevGenerationForm,
            [name]: value,
        }))
    }

    const buildGenerationPayload = () => ({
        packageCode: (generationForm.packageCode || '').trim().toUpperCase(),
        regionCode: generationForm.regionCode,
        channel: generationForm.channel,
        batchNo: (generationForm.batchNo || '').trim().toUpperCase(),
        quantity: Number(generationForm.quantity),
    })

    const validateGenerationPayload = (payload) => {
        if (!payload.packageCode) {
            return '패키지 코드를 입력해주세요.'
        }

        if (!payload.regionCode) {
            return '구를 선택해주세요.'
        }

        if (!payload.channel) {
            return '판매 채널을 선택해주세요.'
        }

        if (!payload.batchNo || payload.batchNo.length > 50) {
            return '배치번호는 1~50자로 입력해주세요.'
        }

        if (!Number.isInteger(payload.quantity) || payload.quantity < 1 || payload.quantity > 5000) {
            return '코드 수량은 1~5000개로 입력해주세요.'
        }

        return ''
    }

    const requestCodeGeneration = async (type) => {
        if (generationLoading) {
            return
        }

        const payload = buildGenerationPayload()
        const validationMessage = validateGenerationPayload(payload)

        if (validationMessage) {
            showSnackbar('error', validationMessage)
            return
        }

        if (type === 'generate') {
            const regionLabel = getRegionLabel(payload.regionCode)
            const confirmed = window.confirm(`${payload.packageCode} ${regionLabel} ${payload.channel} / ${payload.batchNo} 코드 ${payload.quantity}개를 생성합니다. 진행할까요?`)

            if (!confirmed) {
                return
            }
        }

        try {
            setGenerationLoading(true)
            const url = type === 'preview'
                ? '/api/v1/admin/animalPackage/previewCodeGeneration'
                : '/api/v1/admin/animalPackage/generateCodes'
            const { data: response } = await API.post(url, payload)

            if ([1, 200].includes(Number(response.code))) {
                setGenerationResult(response.data)
                showSnackbar('success', type === 'preview' ? '코드 생성 미리보기를 완료했습니다.' : '코드를 생성했습니다.')

                if (type === 'generate') {
                    getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', response.message || '코드 생성 요청에 실패했습니다.')
            }
        } catch (_error) {
            showSnackbar('error', '코드 생성 요청에 실패했습니다.')
        } finally {
            setGenerationLoading(false)
        }
    }

    const exportToExcel = async () => {
        if (excelLoading) {
            return
        }

        try {
            setExcelLoading(true)
            const { data: response } = await API.post('/api/v1/admin/animalPackage/getExcelCodeList', {
                searchText: searchQuery,
                searchSelect,
            })

            if ([1, 200].includes(Number(response.code))) {
                const rows = response.data || []

                if (rows.length === 0) {
                    showSnackbar('error', '엑셀 데이터가 없습니다.')
                    return
                }

                const ws = XLS.utils.json_to_sheet(rows)
                const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
                const excelBuffer = XLS.write(wb, { bookType: 'xlsx', type: 'array' })
                const excelData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' })
                FileSaver.saveAs(excelData, `animal-package-codes-${formatDate(new Date())}.xlsx`)
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', response.message || '엑셀 다운로드에 실패했습니다.')
            }
        } catch (_error) {
            showSnackbar('error', '엑셀 다운로드에 실패했습니다.')
        } finally {
            setExcelLoading(false)
        }
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="패키지 코드 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={isBusy}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 패키지 코드가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 패키지 코드가 없습니다."
                description="구와 판매 채널을 선택해 코드를 생성하면 이 목록에 표시됩니다."
            />
        )
    }

    const renderGenerationResult = () => {
        if (!generationResult) {
            return null
        }

        const codes = generationResult.codes || generationResult.sampleCodes || []
        const existingCount = generationResult.existingCount || generationResult.existingCountAfter

        return (
            <Alert severity={generationResult.previewOnly ? 'info' : 'success'} sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {generationResult.previewOnly ? '미리보기' : '생성 완료'}: {generationResult.regionName} / {generationResult.channel} / {generationResult.batchNo} / {generationResult.quantity}개
                </Typography>
                {existingCount && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        기존 코드: 총 {existingCount.total || 0}개, 미사용 {existingCount.generated || 0}개, 활성화 {existingCount.activated || 0}개, 폐기 {existingCount.void || 0}개
                    </Typography>
                )}
                {codes.length > 0 && (
                    <Box sx={resultCodeListSx}>
                        {codes.slice(0, 20).map((item) => (
                            <Box key={item.code} component="span" sx={CODE_TEXT_SX}>{item.code}</Box>
                        ))}
                        {codes.length > 20 && <Box component="span">외 {codes.length - 20}개</Box>}
                    </Box>
                )}
            </Alert>
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner />
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose} />}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>패키지 활성화 코드 관리</Title>

                    <Box sx={generationPanelSx}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            코드 생성
                        </Typography>
                        <Box sx={generationFormSx}>
                            <TextField
                                label="패키지 코드"
                                name="packageCode"
                                value={generationForm.packageCode}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ width: 150 }}
                            />
                            <TextField
                                label="구"
                                select
                                name="regionCode"
                                value={generationForm.regionCode}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ minWidth: 190 }}
                            >
                                <MenuItem value="">선택</MenuItem>
                                {SEOUL25_REGIONS.map((region) => (
                                    <MenuItem key={region.regionCode} value={region.regionCode}>
                                        {region.regionName} ({region.codePrefix})
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="판매 채널"
                                select
                                name="channel"
                                value={generationForm.channel}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                sx={{ minWidth: 130 }}
                            >
                                <MenuItem value="RM">RM</MenuItem>
                                <MenuItem value="KAKAO">KAKAO</MenuItem>
                            </TextField>
                            <TextField
                                label="배치번호"
                                name="batchNo"
                                value={generationForm.batchNo}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                inputProps={{ maxLength: 50 }}
                                sx={{ width: 220 }}
                            />
                            <TextField
                                label="수량"
                                name="quantity"
                                type="number"
                                value={generationForm.quantity}
                                onChange={updateGenerationForm}
                                size="small"
                                disabled={generationLoading}
                                inputProps={{ min: 1, max: 5000 }}
                                sx={{ width: 120 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={generationLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => requestCodeGeneration('preview')}
                            >
                                미리보기
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<Add />}
                                disabled={generationLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => requestCodeGeneration('generate')}
                            >
                                생성
                            </Button>
                        </Box>
                        {renderGenerationResult()}
                    </Box>

                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="활성화 코드, Prefix 또는 배치번호"
                                type="search"
                                name="searchText"
                                value={searchText}
                                disabled={isBusy}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                            <TextField
                                label="패키지 코드"
                                name="packageCode"
                                value={searchSelect.packageCode}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ width: 150 }}
                            />
                            <TextField
                                label="구"
                                select
                                name="regionCode"
                                value={searchSelect.regionCode}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ minWidth: 170 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                {SEOUL25_REGIONS.map((region) => (
                                    <MenuItem key={region.regionCode} value={region.regionCode}>
                                        {region.regionName} ({region.codePrefix})
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="채널"
                                select
                                name="channel"
                                value={searchSelect.channel}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="RM">RM</MenuItem>
                                <MenuItem value="KAKAO">KAKAO</MenuItem>
                            </TextField>
                            <TextField
                                label="상태"
                                select
                                name="status"
                                value={searchSelect.status}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={{ minWidth: 130 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="generated">생성</MenuItem>
                                <MenuItem value="activated">활성화</MenuItem>
                                <MenuItem value="void">폐기</MenuItem>
                            </TextField>
                            <TextField
                                label="배치번호"
                                name="batchNo"
                                value={searchSelect.batchNo}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                inputProps={{ maxLength: 50 }}
                                sx={{ width: 220 }}
                            />
                        </ListFilterForm>
                    </ListFilterBar>

                    <ListTableToolbar
                        left={(
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                disabled={isBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={exportToExcel}
                            >
                                엑셀
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={CODE_TABLE_SX}>
                            <colgroup>
                                {CODE_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`animal-package-code-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>상품명</TableCell>
                                    <TableCell>구</TableCell>
                                    <TableCell>채널</TableCell>
                                    <TableCell>배치번호</TableCell>
                                    <TableCell>코드</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell>활성화 유저</TableCell>
                                    <TableCell>생성일</TableCell>
                                    <TableCell>활성화일</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? data.map((row, index) => (
                                    <TableRow key={row.animalPackageCodeId}>
                                        <TableCell sx={NOWRAP_CELL_SX}>{(curPage - 1) * offset + index + 1}</TableCell>
                                        <TableCell>{renderTitle(row.productTitle)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{getRegionLabel(row.regionCode, row.regionName)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{getChannelChip(row.channel)}</TableCell>
                                        <TableCell sx={BATCH_NO_CELL_SX}>{row.batchNo || '-'}</TableCell>
                                        <TableCell sx={CODE_TEXT_SX}>{row.code || '-'}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{getStatusChip(row.status)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{row.activatedUserId || '-'}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{formatDateTimeText(row.regTime)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{formatDateTimeText(row.activatedAt)}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={10}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose} />}
                </Paper>
            )}
        </Grid>
    )
}
